/**
 * Value-to-height scale shared by the web and mobile stats charts: where a value sits in the plot,
 * as a fraction of the plot's height measured up from its bottom edge.
 */

/** Where every value sits when the range is empty, so a caller never divides by zero. */
const EMPTY_RANGE_FRACTION = 0.5;

/** A bar's extent in the plot, as fractions of the plot's height measured up from its bottom edge. */
export interface BarSpan {
  bottom: number;
  height: number;
}

/** The value's height in the plot, 0 at `minValue` and 1 at `maxValue`, clamped to that range. */
export function plotFraction(value: number, minValue: number, maxValue: number): number {
  const range = maxValue - minValue;
  if (range <= 0) return EMPTY_RANGE_FRACTION;
  return Math.min(Math.max((value - minValue) / range, 0), 1);
}

/**
 * A bar drawn from the zero line to the value. The zero line is clamped into the range, so a range
 * wholly above zero grows its bars from the bottom edge, and a negative value hangs below zero.
 */
export function barSpan(value: number, minValue: number, maxValue: number): BarSpan {
  const zero = plotFraction(0, minValue, maxValue);
  const end = plotFraction(value, minValue, maxValue);
  return { bottom: Math.min(zero, end), height: Math.abs(end - zero) };
}
